/**
 * useSidebarCollapse — the web shell's foldable-sidebar state, persisted to
 * localStorage so the recruiter's choice survives reloads. Best-effort
 * persistence: any storage failure (private mode, SSR, tests) falls back to the
 * in-memory default without throwing.
 */
import { useCallback, useEffect, useState } from "react";

const STORAGE_KEY = "nu-assist:web:sidebar-collapsed";

function readPersisted(): boolean {
  try {
    return globalThis.localStorage?.getItem(STORAGE_KEY) === "1";
  } catch {
    return false;
  }
}

export interface SidebarCollapse {
  collapsed: boolean;
  toggle: () => void;
}

export function useSidebarCollapse(): SidebarCollapse {
  const [collapsed, setCollapsed] = useState<boolean>(readPersisted);

  useEffect(() => {
    try {
      globalThis.localStorage?.setItem(STORAGE_KEY, collapsed ? "1" : "0");
    } catch {
      // persistence is best-effort; ignore storage failures.
    }
  }, [collapsed]);

  const toggle = useCallback(() => setCollapsed((c) => !c), []);
  return { collapsed, toggle };
}
